// The Assets / Debts / Net worth summary block that closes the account list
// on `#s01` -- the visible-summation property the design delivers instead of
// per-class subtotals (execution plan §4 rule 5 / §9 conflict #11). Assets
// and debts are a pure sign split over known balances, never a `kind`
// negation; the net-worth row repeats the same three-shape rollup as
// `NetWorthHeader` so the two never disagree.
import type { Account } from '@/api/hooks/useAccounts';
import { AmountText } from '@/components/AmountText';
import { computeNetWorthRollup } from './netWorthRollup';

export function AccountsSummaryRows({
  accounts,
  className,
  aggregateDisplay = 'physical',
}: {
  accounts: Account[];
  className?: string;
  aggregateDisplay?: 'physical' | 'canonical_unavailable';
}) {
  if (aggregateDisplay === 'canonical_unavailable') {
    return (
      <dl
        className={['grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 rounded-lg bg-muted px-3 py-3 text-sm', className]
          .filter(Boolean)
          .join(' ')}
        data-testid="accounts-summary-rows"
      >
        <dt className="text-muted-foreground">Assets</dt>
        <dd />
        <dt className="text-muted-foreground">Debts</dt>
        <dd />
        <dt className="font-semibold text-foreground">Net worth</dt>
        <dd className="text-right font-semibold" data-testid="canonical-unavailable-status">Account totals are unavailable.</dd>
      </dl>
    );
  }

  const rollup = computeNetWorthRollup(accounts);
  const knownCount = accounts.length - rollup.neverReportedCount;
  const partial = rollup.shape === 'partial';

  return (
    <dl
      className={['grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 rounded-lg bg-muted px-3 py-3 text-sm', className]
        .filter(Boolean)
        .join(' ')}
      data-testid="accounts-summary-rows"
    >
      <dt className="text-muted-foreground">
        Assets{knownCount < accounts.length ? ` (${knownCount} of ${accounts.length} reported)` : ''}
      </dt>
      <dd className="text-right">
        <AmountText value={rollup.assetsTotal === null ? null : String(rollup.assetsTotal)} variant="balance" />
      </dd>
      <dt className="text-muted-foreground">Debts</dt>
      <dd className="text-right">
        <AmountText value={rollup.debtsTotal === null ? null : String(rollup.debtsTotal)} variant="balance" />
      </dd>
      <dt className="font-semibold text-foreground">Net worth{partial ? ' · partial' : ''}</dt>
      <dd className="text-right font-semibold">
        {rollup.total === null ? (
          <span className="text-degraded-foreground">unavailable</span>
        ) : (
          <AmountText value={String(rollup.total)} variant="balance" balanceStatus={partial ? 'stale' : 'ok'} />
        )}
      </dd>
    </dl>
  );
}

export default AccountsSummaryRows;
